import type { InstantConfigRepository } from "instant-config";
import {
  getMonetizationTier,
  MonetizationTier,
} from "../helpers/getMonetizationTier";

/**
 * This type is used to represent the parameters that have different values
 * for different monetization tiers.
 */
export type TieredParams<T> =
  | T
  | {
      [MonetizationTier.PARTIALLY_MONETIZED]?: Partial<T>;
      [MonetizationTier.FULLY_MONETIZED]?: Partial<T>;
    };

/**
 * This type is used to represent the parameters that are not tiered.
 * It is used to filter out the tiered parameters from the `params` object.
 * It is useful for cases where you want to get the default parameters
 * that are not specific to any monetization tier.
 */
export type UntieredParam<T> = Omit<
  T,
  MonetizationTier.PARTIALLY_MONETIZED | MonetizationTier.FULLY_MONETIZED
>;

export type MonetizationTierEngineConfig = Pick<
  InstantConfigRepository,
  "icTieredMonetizationEnabled"
>;

export class MonetizationTierEngine {
  static readonly DEFAULT_MONETIZATION_TIER =
    MonetizationTier.PARTIALLY_MONETIZED;
  /**
   * The current monetization tier for the page.
   * This is determined by the `fandomContext` object, which is set by the UCP.
   * If the monetization tier is not set or is set to an invalid value,
   * it will fall back to the default monetization tier.
   */
  private currentTier!: MonetizationTier;

  constructor(private readonly config: MonetizationTierEngineConfig) {
    this.readCurrentTier();
  }

  private readCurrentTier(): void {
    // If the monetization tier mechanism is not enabled,
    // default to FULLY_MONETIZED.
    if (!this.config.icTieredMonetizationEnabled) {
      console.warn(
        "MonetizationTierEngine: Tiered monetization is not enabled. Defaulting to FULLY_MONETIZED.",
      );
      this.currentTier = MonetizationTier.FULLY_MONETIZED;
      return;
    }

    this.currentTier = getMonetizationTier(window?.fandomContext ?? null);
    // Check if the monetization tier returned from the context is not valid,
    // we will fall back to the default monetization tier.
    // This is to ensure that the ad engine can continue to function
    // even if the monetization tier is not set or is set to an invalid value.
    if (
      this.currentTier !== MonetizationTier.PARTIALLY_MONETIZED &&
      this.currentTier !== MonetizationTier.FULLY_MONETIZED
    ) {
      console.warn(
        `MonetizationTierEngine: Invalid monetization tier value: ${this.currentTier}. Falling back to default tier: ${MonetizationTierEngine.DEFAULT_MONETIZATION_TIER}.`,
      );

      this.currentTier = MonetizationTierEngine.DEFAULT_MONETIZATION_TIER;
    }
  }

  /**
   * Filter tiered parameters based on the current monetization tier.
   * This function will return the parameters for the current tier.
   *
   * In the case when the `params` object does not contain tiered parameters,
   * it will return the `params` object as is.
   *
   * This is useful for cases where you have different parameters
   * for different monetization tiers, and you want to filter them
   * based on the current tier.
   *
   * The schema of the `params` object is as follows:
   * ```ts
   * type TieredParams<T> = {
   *  [MonetizationTier.PARTIALLY_MONETIZED]?: T;
   *  [MonetizationTier.FULLY_MONETIZED]?: T;
   *  };
   * ```
   *
   * Example 1:
   * Given:
   * ```ts
   * const params = {
   *  [MonetizationTier.PARTIALLY_MONETIZED]: { param1: "value1" },
   *  [MonetizationTier.FULLY_MONETIZED]: { param1: "value2" },
   * };
   * const filteredParams = filterTieredParamsByCurrentTier(params);
   * ```
   * If the current tier is `PARTIALLY_MONETIZED`, the result will be:
   * ```ts
   * {
   * param1: "value1",
   * }
   * ```
   *
   * Example 2a:
   * Given:
   * ```ts
   * const params = {
   *  [MonetizationTier.PARTIALLY_MONETIZED]: { param1: "value1" },
   * };
   * }
   * const filteredParams = filterTieredParamsByCurrentTier(params);
   * ```
   * If the current tier is `PARTIALLY_MONETIZED`, the result will be:
   * ```ts
   * {
   * param1: "value1",
   * }
   * ```
   *
   * Example 2b:
   * Given:
   * ```ts
   * const params = {
   *  [MonetizationTier.PARTIALLY_MONETIZED]: { param1: "value1" },
   * };
   * }
   * const filteredParams = filterTieredParamsByCurrentTier(params);
   * ```
   * If the current tier is `FULLY_MONETIZED`, it will return `undefined`.
   *
   * Example 3:
   * Given:
   * ```ts
   * const params = {
   *   param1: "value1",
   * };
   * const filteredParams = filterTieredParamsByCurrentTier(params);
   * ```
   * This is incorrect usage, but if you pass a non-tiered object,
   * it will return `undefined`.
   *
   * @param params The parameters to filter based on the current monetization tier.
   * @returns The filtered parameters for the current tier.
   */
  // eslint-disable-next-line complexity
  filterTieredParamsByCurrentTier<T = Record<string, unknown>>(
    params: TieredParams<T>,
  ): T | undefined {
    /**
     * Validation:
     * - If `params` is undefined or does not contain any tiered parameters, return empty object.
     * - If the monetization tier mechanism is not enabled or the current tier is not set,
     *    return empty object.
     * - If the current tier is not set,  return empty object.
     */
    if (
      !params ||
      typeof params !== "object" ||
      params === null ||
      Array.isArray(params)
    ) {
      return {} as T;
    }

    // Extract the tiered parameters for partially and fully monetized tiers
    const partialMonetizationParams =
      MonetizationTier.PARTIALLY_MONETIZED in params
        ? (params[MonetizationTier.PARTIALLY_MONETIZED] as T)
        : undefined;
    const fullMonetizationParams =
      MonetizationTier.FULLY_MONETIZED in params
        ? (params[MonetizationTier.FULLY_MONETIZED] as T)
        : undefined;

    // If both monetization parameters are undefined, we will return undefined
    if (
      partialMonetizationParams === undefined &&
      fullMonetizationParams === undefined
    ) {
      return undefined;
    }

    // Get the current monetization tier
    const currentTier = this.getCurrentTier();

    // If the current tier is `PARTIALLY_MONETIZED`, we will return the
    // partially monetized parameters merged with the default parameters.
    if (currentTier === MonetizationTier.PARTIALLY_MONETIZED) {
      return partialMonetizationParams;
    }
    // If the current tier is `FULLY_MONETIZED`, we will return the
    // fully monetized parameters merged with the default parameters.
    if (currentTier === MonetizationTier.FULLY_MONETIZED) {
      return fullMonetizationParams;
    }

    return undefined;
  }

  /**
   * Checks if the monetization tier mechanism is enabled based on the provided configuration.
   * @returns `true` if the monetization tier mechanism is enabled, `false` otherwise.
   */
  isEnabled(): boolean {
    return this.config.icTieredMonetizationEnabled;
  }

  /**
   * Returns the current monetization tier.
   * @returns The current monetization tier.
   * @note This differs from `getMonetizationTier` in that it returns the tier
   * based on the current configuration and if anything goes wrong, it will
   * return the default tier instead.
   */
  public getCurrentTier(): MonetizationTier {
    return this.currentTier;
  }

  /**
   * Checks if the current monetization tier is either partially or fully monetized.
   * @returns `true` if the page is monetized, `false` otherwise.
   */
  public isMonetized(): boolean {
    return this.isPartiallyMonetized() || this.isFullyMonetized();
  }

  /**
   * Checks if the current monetization tier is partially monetized.
   * @returns `true` if the page is partially monetized, `false` otherwise.
   */
  public isPartiallyMonetized(): boolean {
    return this.getCurrentTier() === MonetizationTier.PARTIALLY_MONETIZED;
  }

  /**
   * Checks if the current monetization tier is fully monetized.
   * @returns `true` if the page is fully monetized, `false` otherwise.
   */
  public isFullyMonetized(): boolean {
    return this.getCurrentTier() === MonetizationTier.FULLY_MONETIZED;
  }

  /**
   * Checks if the current monetization tier is demonetized.
   * @returns `true` if the page is demonetized, `false` otherwise.
   * @note This is the opposite of {@link isMonetized()}.
   */
  public isDemonetized(): boolean {
    return !this.isMonetized();
  }
}
